import { HttpClientInterface } from "../index";
import { customerFactory } from "../common/models/Customer";
import { APIResponse, parseItemsResponse, parseItemResponse } from "../ModelFactory";
import { domainFactory } from "./models/Domain";
import { DNSRecord, DNSZone, dnsZoneFactory } from "./models/DNSZone";
import { searchResultFactory } from "./models/SearchResult";
import { domainPriceFactory } from "./models/DomainPrice";
import { serverFactory } from "./models/Server";
import { siteFactory } from "./models/Site";
import { sshKeyFactory } from "./models/SshKey";
import { siteOptimizeReportFactory } from "./models/SiteOptimizeReport";
import { Deployment, deploymentFactory } from "./models/Deployment";
import { permissionMapFactory } from "../common/models/PermissionMap";
import { historicDomainPriceFactory } from "./models/HistoricDomainPrice";
import { mailDomainFactory } from "./models/MailDomain";
import { roleFactory } from "./models/Role";

export class PartnerApiClient {
  constructor(private http: HttpClientInterface) { }

  /**
   * Customer
   */
  public async getAllCustomers() {
    const json = await this.http.get<{ items: object[] }>("customers");
    return parseItemsResponse(json, customerFactory);
  }
  public async createCustomer(data: object) {
    const json = await this.http.post<{ item: object }>("customers", data);
    return parseItemResponse(json, customerFactory);
  }
  public async getCustomer(uuid: string) {
    const json = await this.http.get<{ items: object[] }>(`customers/${uuid}`);
    return parseItemResponse(json, customerFactory);
  }
  public async updateCustomer(uuid: string, data: object) {
    const json = await this.http.put<{ items: object[] }>(`customers/${uuid}`, data);
    return parseItemResponse(json, customerFactory);
  }

  /**
   * Customer Contact
   */
  public async createCustomerContact(customerUuid: string, data: object) {
    const json = await this.http.post<{ item: object }>(`customers/${customerUuid}/contacts`, data);
    return parseItemResponse(json, customerFactory);
  }
  public async updateCustomerContact(customerUuid: string, contactUuid: string, data: object) {
    const json = await this.http.put<{ item: object }>(`customers/${customerUuid}/contacts/${contactUuid}`, data);
    return parseItemResponse(json, customerFactory);
  }
  public async deleteCustomerContact(customerUuid: string, contactUuid: string) {
    const json = await this.http.delete<{ item: object }>(`customers/${customerUuid}/contacts/${contactUuid}`);
    return parseItemResponse(json, deploymentFactory);
  }
  public async inviteCustomerContact(customerUuid: string, contactUuid: string, roleUuid?: string): Promise<APIResponse<{ newUser: boolean, deployment: Deployment }>> {
    const json = await this.http.post<{ data?: { new_user: boolean }, item?: object, message?: string }>(
      `customers/${customerUuid}/contacts/${contactUuid}/user`,
      roleUuid ? { role_uuid: roleUuid } : {},
    );
    if (typeof json?.data?.new_user !== "boolean") {
      return { success: false, error: json?.message ?? "Could not grant login access", errors: [] };
    }
    const deployment = parseItemResponse(json, deploymentFactory);
    if (!deployment.success) {
      return deployment;
    }
    return { success: true, data: { newUser: json.data.new_user, deployment: deployment.data } };
  }
  public async updateCustomerContactUserRole(customerUuid: string, contactUuid: string, roleUuid: string) {
    const json = await this.http.put<{ item: object }>(`customers/${customerUuid}/contacts/${contactUuid}/user/role`, {
      role_uuid: roleUuid,
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async revokeCustomerContactAccess(customerUuid: string, contactUuid: string) {
    const json = await this.http.delete<{ item: object }>(`customers/${customerUuid}/contacts/${contactUuid}/user`);
    return parseItemResponse(json, deploymentFactory);
  }
  public async resetCustomerContactPassword(customerUuid: string, contactUuid: string, password: string) {
    await this.http.post<Record<string, never>>(`customers/${customerUuid}/contacts/${contactUuid}/user/password`, {
      password,
      temporary: true,
    });
  }

  /**
   * Domain
   */
  public async getAllDomains() {
    const json = await this.http.get<{ items: object[] }>("domains");
    return parseItemsResponse(json, domainFactory);
  }
  public async createDomain(data: object) {
    const json = await this.http.post<{ item: object }>("domains", data);
    return parseItemResponse(json, deploymentFactory);
  }
  public async getDomain(uuid: string) {
    const json = await this.http.get<{ items: object[] }>(`domains/${uuid}`);
    return parseItemResponse(json, domainFactory);
  }
  public async getDomainPrice(domain: string) {
    const json = await this.http.get<{ items: object[] }>(`domains/price/${domain}`);
    return parseItemResponse(json, domainPriceFactory);
  }
  public async getDomainPriceTlds() {
    const json = await this.http.get<{ items: string[] }>(`domains/prices/tlds`);
    return json.items;
  }
  public async getDomainPriceTldHistory(tld: string) {
    const json = await this.http.get<{ items: object[] }>(`domains/prices/tlds/${tld}`);
    return parseItemsResponse(json, historicDomainPriceFactory);
  }
  public async updateWhoisContacts(uuid: string, owner: string, admin: string, tech: string, reseller: string) {
    const json = await this.http.put<{ items: object[] }>(`domains/${uuid}`, {
      domain_owner: owner,
      domain_admin: admin,
      domain_tech: tech,
      domain_reseller: reseller,
    });
    return parseItemResponse(json, domainFactory);
  }
  public async getDomainTransferSecret(uuid: string): Promise<APIResponse<string>> {
    const json = await this.http.get<{ item?: { code: string; type: string }, message?: string }>(`domains/${uuid}/auth_code`);
    if (typeof json?.item?.code !== "string") {
      return { success: false, error: json?.message ?? "Could not request the transfer secret", errors: [] };
    }
    return { success: true, data: json.item.code };
  }
  public async enableTransferLock(uuid: string) {
    const json = await this.http.post<{ item: object }>(`domains/${uuid}/enable_transfer_lock`, {});
    return parseItemResponse(json, deploymentFactory);
  }
  public async disableTransferLock(uuid: string) {
    const json = await this.http.post<{ item: object }>(`domains/${uuid}/disable_transfer_lock`, {});
    return parseItemResponse(json, deploymentFactory);
  }
  public async getDNSZone(uuid: string): Promise<APIResponse<DNSZone>> {
    const json = await this.http.get<{ items: object[] }>(`domains/${uuid}/dns`);
    return parseItemResponse(json, dnsZoneFactory);
  }
  public async useExternalNameservers(uuid: string, nameservers: string[]) {
    const json = await this.http.post<{ item: object }>(`domains/${uuid}/nameservers/external`, {
      nameservers: nameservers,
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async useInternalNameservers(uuid: string) {
    const json = await this.http.post<{ item: object }>(`domains/${uuid}/nameservers/internal`, {});
    return parseItemResponse(json, deploymentFactory);
  }
  public async updateDNSZone(uuid: string, added: DNSRecord[], updated: DNSRecord[], deleted: DNSRecord[]) {
    const json = await this.http.post<{ item: object }>(`domains/${uuid}/dns`, {
      add: added.map((r) => {
        switch (r.type) {
          case "A":
          case "AAAA":
          case "CNAME":
            return {
              type: r.type,
              name: r.name,
              content: r.content,
              proxied: r.proxied,
              ttl: r.ttl,
            };
          case "MX":
          case "REDIRECT":
            return {
              type: r.type,
              name: r.name,
              content: r.content,
              priority: r.priority,
              ttl: r.ttl,
            };
          case "CAA":
            return {
              type: r.type,
              name: r.name,
              data: {
                flags: r.flags,
                tag: r.tag,
                value: r.value,
              },
              ttl: r.ttl,
            };
          case "TLSA":
            return {
              type: r.type,
              name: r.name,
              data: {
                usage: r.usage,
                selector: r.selector,
                matching_type: r.matchingType,
                certificate: r.certificate,
              },
              ttl: r.ttl,
            };
          case "HTTPS":
            return {
              type: r.type,
              name: r.name,
              data: {
                priority: r.priority,
                target: r.target,
                value: r.value,
              },
              ttl: r.ttl,
            };
          case "SRV":
            return {
              type: r.type,
              name: r.name,
              data: {
                name: r.name,
                service: r.service,
                proto: r.proto,
                priority: r.priority,
                weight: r.weight,
                port: r.port,
                target: r.target,
              },
              ttl: r.ttl,
            };
          default:
            return {
              type: r.type,
              name: r.name,
              content: r.content,
              ttl: r.ttl,
            };
        }
      }),
      edit: updated.map((r) => {
        switch (r.type) {
          case "A":
          case "AAAA":
          case "CNAME":
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              content: r.content,
              proxied: r.proxied,
              ttl: r.ttl,
            };
          case "MX":
          case "REDIRECT":
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              content: r.content,
              priority: r.priority,
              ttl: r.ttl,
            };
          case "CAA":
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              data: {
                flags: r.flags,
                tag: r.tag,
                value: r.value,
              },
              ttl: r.ttl,
            };
          case "TLSA":
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              data: {
                usage: r.usage,
                selector: r.selector,
                matching_type: r.matchingType,
                certificate: r.certificate,
              },
              ttl: r.ttl,
            };
          case "HTTPS":
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              data: {
                priority: r.priority,
                target: r.target,
                value: r.value,
              },
              ttl: r.ttl,
            };
          case "SRV":
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              data: {
                name: r.name,
                service: r.service,
                proto: r.proto,
                priority: r.priority,
                weight: r.weight,
                port: r.port,
                target: r.target,
              },
              ttl: r.ttl,
            };
          default:
            return {
              id: r.id,
              type: r.type,
              name: r.name,
              content: r.content,
              ttl: r.ttl,
            };
        }
      }),
      delete: deleted.map((r) => ({ id: r.id })),
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async exportDnsZone(uuid: string) {
    return this.http.getFile(`domains/${uuid}/dns/export`);
  }
  public async validateImportDnsZone(uuid: string, zoneFile: File) {
    const json = await this.http.postFile<unknown>(`domains/${uuid}/dns/import?dry_run=1`, zoneFile, "file", "json");
    return parseItemResponse(json, dnsZoneFactory);
  }
  public async importDnsZone(uuid: string, zoneFile: File) {
    await this.http.postFile<unknown>(`domains/${uuid}/dns/import?dry_run=0`, zoneFile, "file", "json");
  }
  /**
   * Dashboard
   */
  public async getDashboardStats() {
    const json = await this.http.get<{
      data: { n_domains: number; n_customers: number; n_hosting_servers: number; n_hosting_subscriptions: number };
    }>("dashboard");
    return {
      nDomains: json["data"]["n_domains"],
      nCustomers: json["data"]["n_customers"],
      nHostingServers: json["data"]["n_hosting_servers"],
      nHostingSubscriptions: json["data"]["n_hosting_subscriptions"],
    };
  }
  public async search(type: string | null, query: string) {
    const json = await this.http.get<{ items: object[] }>(
      "search?query=" + encodeURIComponent(query) + (type ? "&type=" + encodeURIComponent(type) : ""),
    );
    return parseItemsResponse(json, searchResultFactory);
  }
  public async updateNotes(uuid: string, notes: string | null) {
    await this.http.put(`entity/${uuid}/note`, {
      notes,
    });
  }

  /**
   * Servers
   */
  public async getAllServers() {
    const json = await this.http.get<{ items: object[] }>("servers");
    return parseItemsResponse(json, serverFactory);
  }
  public async getServer(serverId: string) {
    const json = await this.http.get<{ item: object }>(`servers/${serverId}`);
    return parseItemResponse(json, serverFactory);
  }
  public async getAvailablePhpVersions(serverId: string): Promise<{ [slug: string]: string }> {
    const json = await this.http.get<{ items: { [slug: string]: string } }>(`servers/${serverId}/php_versions`);
    return json.items;
  }
  public async updateServerDefaultNginxConfig(
    serverId: string,
    defaultNginxConfig: string,
  ): Promise<APIResponse<Deployment>> {
    const json = await this.http.put<{ item: object }>(`servers/${serverId}`, {
      default_nginx_configuration: defaultNginxConfig,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async getAllMailDomains() {
    const json = await this.http.get<{ items: object[] }>("mail");
    return parseItemsResponse(json, mailDomainFactory);
  }
  public async getMailDomain(id: string) {
    const json = await this.http.get<{ items: object[] }>(`mail/${id}`);
    return parseItemResponse(json, mailDomainFactory);
  }
  public async createMailDomain(domain: string, customerId: string) {
    const json = await this.http.post<{ item: object }>("mail", {
      domain,
      customer: customerId,
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async addUserToMailDomain(domainId: string, email: string, password: string) {
    const json = await this.http.post<{ item: object }>(`mail/${domainId}/user`, {
      email,
      password,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async deleteUserFromMailDomain(mail: string, address: string) {
    await this.http.delete<void>(`/mail/${mail}/user/${address}`);
  }

  public async resetPasswordFromUser(mail: string, address: string, password: string) {
    const json = await this.http.put<{ item: object }>(`/mail/${mail}/user/${address}`, {
      password,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async deleteAliasFromMailDomain(mail: string, alias: number) {
    await this.http.delete<void>(`/mail/${mail}/aliases/${alias}`);
  }

  public async deleteForwardFromMailDomain(mail: string, forward: number) {
    await this.http.delete<void>(`/mail/${mail}/forwards/${forward}`);
  }

  public async addAliasToMailDomain(mail: string, email: string, alias: string) {
    const json = await this.http.post<{ item: object }>(`/mail/${mail}/aliases`, {
      email,
      alias,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async addForwardToMailDomain(mail: string, email: string, target: string) {
    const json = await this.http.post<{ item: object }>(`/mail/${mail}/forwards/`, {
      email,
      target,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async createTransactionalMailDomain(domain: string, customerId: string) {
    const json = await this.http.post<{ item: object }>("transactional-mail", {
      domain,
      customer: customerId,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async addAliasToTransactionalMailDomain(mail: string, domain: string) {
    const json = await this.http.post<{ item: object }>(`transactional-mail/${mail}/aliases`, {
      domain,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async deleteAliasFromTransactionalMailDomain(mail: string, domain: string) {
    const json = await this.http.delete<{ item: object }>(`transactional-mail/${mail}/aliases/${encodeURIComponent(domain)}`);
    return parseItemResponse(json, deploymentFactory);
  }

  public async refreshTransactionalMailDomainRecords(mail: string) {
    const json = await this.http.post<{ item: object }>(`transactional-mail/${mail}/records/refresh`, {});
    return parseItemResponse(json, deploymentFactory);
  }

  public async addUserToTransactionalMailDomain(mail: string, email: string): Promise<APIResponse<{ deployment: Deployment; smtpPassword: string }>> {
    const json = await this.http.post<{ item: object; smtp_password: string }>(`transactional-mail/${mail}/users`, {
      email,
    });
    const deploymentResponse = parseItemResponse(json, deploymentFactory);
    if (!deploymentResponse.success) {
      return deploymentResponse;
    }
    return { success: true, data: { deployment: deploymentResponse.data, smtpPassword: json.smtp_password } };
  }

  public async deleteUserFromTransactionalMailDomain(mail: string, userId: number) {
    const json = await this.http.delete<{ item: object }>(`transactional-mail/${mail}/users/${userId}`);
    return parseItemResponse(json, deploymentFactory);
  }

  /**
   * Sites
   */
  public async getAllSites() {
    // TODO: Make dynamic
    const json = await this.http.get<{ items: object[] }>("sites");
    // const json = {
    //   items: [
    //     {uuid: 'abc', tenant_uuid: 'cabe94bf-b74b-4541-87c1-794a67ee6fe0', customer_uuid: '0029bc42-9ed6-4a6c-b564-adbf853852a7', display_name: 'blog.jessekramer.dev', deployment_status: 'success', server_uuid: 'abc'},
    //   ]
    // }
    return parseItemsResponse(json, siteFactory);
  }
  public async getSite(uuid: string) {
    const json = await this.http.get<{ item: object }>(`sites/${uuid}`);
    return parseItemResponse(json, siteFactory);
  }
  public async getSiteManageUrl(uuid: string) {
    const json = await this.http.get<{ item: string }>(`sites/${uuid}/manage`);
    return json.item;
  }
  public async createSite(
    hostname: string,
    type: "shared_hosting_server" | "shared_hosting_subscription" | "windhosting_site",
    customerUuid: string,
    serverUuid: string,
    sshKeys: string[],
  ): Promise<APIResponse<Deployment>> {
    const json = await this.http.post<{ item: object }>("sites", {
      address: hostname,
      type,
      customer: customerUuid,
      server_uuid: serverUuid,
      authorized_keys: sshKeys,
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async setSitePhpVersion(siteId: string, phpHandler: string) {
    const json = await this.http.post<{ item: string }>(`sites/${siteId}/php_version`, {
      php_handler: phpHandler,
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async setSiteCustomer(siteId: string, customerId: string) {
    await this.http.post<{ item: string }>(`sites/${siteId}`, {
      customer: customerId,
    });
  }
  public async updateSiteNginxConfig(siteId: string, configuration: string) {
    const json = await this.http.post<{ item: string }>(`sites/${siteId}/nginx_settings`, {
      nginx_settings: configuration,
    });
    return parseItemResponse(json, deploymentFactory);
  }
  public async updateSitePhpConfig(siteId: string, configuration: string) {
    const json = await this.http.post<{ item: string }>(`sites/${siteId}/php_settings`, {
      php_settings: configuration,
    });
    return parseItemResponse(json, deploymentFactory);
  }

  public async getSiteOptimizeReport(uuid: string) {
    const json = await this.http.get<object>(`sites/${uuid}/optimize`);
    return parseItemResponse(json, siteOptimizeReportFactory);
  }
  public async optimizeSite(uuid: string, mode: "only_live" | "live_and_optimize") {
    await this.http.post<Record<string, never>>(`sites/${uuid}/optimize`, {
      mode,
    });
  }
  public async cancelSiteDeletion(siteId: string) {
    const json = await this.http.post<{ item: object }>(`sites/${siteId}/cancel_delete`, {});
    return parseItemResponse(json, deploymentFactory);
  }

  /**
   * SSH Keys
   */
  public async getAllSshKeys() {
    const json = await this.http.get<{ items: object[] }>("authorized_keys");
    return parseItemsResponse(json, sshKeyFactory);
  }
  public async getSshKey(uuid: string) {
    const json = await this.http.get<{ item: object }>(`authorized_keys/${uuid}`);
    return parseItemResponse(json, sshKeyFactory);
  }
  public async createSshKey(
    payload: { customer_contact: string; public_key: string } | { display_name: string; public_key: string },
  ) {
    const json = await this.http.post<{ item: object }>("authorized_keys", payload);
    return parseItemResponse(json, sshKeyFactory);
  }
  public async deleteSshKey(uuid: string) {
    await this.http.delete<void>(`authorized_keys/${uuid}`);
  }
  public async addSshKeyToSite(siteId: string, keyId: string): Promise<void> {
    await this.http.post<void>(`sites/${siteId}/authorized_keys`, {
      authorized_key: keyId,
    });
  }
  public async deleteSshKeyFromSite(siteId: string, keyId: string): Promise<void> {
    await this.http.delete<void>(`sites/${siteId}/authorized_keys/${keyId}`);
  }

  // Deployments
  public async getDeployments(from?: Date, to?: Date) {
    const json = await this.http.get<{ items: object[] }>(
      from !== undefined && to !== undefined
        ? `deployments?from=${Math.floor(from.getTime() / 1000)}&to=${Math.floor(to.getTime() / 1000)}`
        : "deployments",
    );
    return parseItemsResponse(json, deploymentFactory);
  }

  // Contracts
  public async cancelContract(contractUuid: string) {
    const json = await this.http.post<{ item: object }>(`contract/${contractUuid}/cancel`, {});
    return parseItemResponse(json, deploymentFactory);
  }
  public async reactivateContract(contractUuid: string) {
    const json = await this.http.post<{ item: object }>(`contract/${contractUuid}/reactivate`, {});
    return parseItemResponse(json, deploymentFactory);
  }

  // User settings
  public async getMFAStatus() {
    const json = await this.http.get<{ enabled: boolean }>("user/mfa-status");
    return json.enabled;
  }

  public async resetPassword() {
    await this.http.post("user/reset-password", {});
  }

  public async enableMFA() {
    await this.http.post("user/mfa-enable", {});
  }

  public async resetMFA() {
    await this.http.post("user/mfa-reset", {});
  }

  public async resetCustomerUserMFA(customerUuid?: string, contactUuid?: string) {
    await this.http.post(`customers/${customerUuid}/contacts/${contactUuid}/user/2fa`, {
      customer_id: customerUuid,
      customer_contact_id: contactUuid,
    });
  }

  // Roles
  public async getRoles() {
    const json = await this.http.get<{ items: object[] }>("roles");
    return parseItemsResponse(json, roleFactory);
  }

  // Permissions
  public async getPermissions() {
    const json = await this.http.get<{ item: object }>("capabilities");
    return parseItemResponse(json, permissionMapFactory);
  }

  public async deleteSite(site: string, removeAt: string) {
    const json = await this.http.delete<{ item: object }>(`sites/${site}?remove_at=${removeAt}`);
    return parseItemResponse(json, deploymentFactory);
  }
}
